import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import type { InspirationAdviceFilterContent } from '@eneco/cms-types/generated/InspirationAdviceFilter';
import { SentenceContainer } from '@eneco/custom-components/InspirationAdviceFilter';
import { useTranslation } from '@eneco/i18n';
import { Box, Stack } from '@eneco/sparky';

import { useFilter } from '../../InspirationAdviceFilter.context';
import { type AdviceFilterPhrase, itemKey } from '../../types/inspirationAdviceFilter.types';
import { getFiltersFromSessionStorage } from '../../utils/filtersInSessionStorage';
import { FilterPhrase } from '../FilterPhrase/FilterPhrase';


export const FilterSentence: FC = () => {
  const { t } = useTranslation();
  const content = useContent<InspirationAdviceFilterContent>();
  const phrasesList = (content.phrasesList ?? []) as AdviceFilterPhrase[];
  const { filterTags, setFilterTags, setFilterIsManuallyChanged } = useFilter();

  const setFilterPhraseValue = (key: string, value: string) => {
    setFilterIsManuallyChanged(true);

    if (value === '') {
      delete filterTags[key];
      return setFilterTags({ ...filterTags });
    }

    setFilterTags(prevState => ({ ...prevState, [key]: value }));
  };

  return (
    <Box paddingBottom={{ initial: '10', lg: '12' }} paddingTop={{ initial: '10', lg: '12' }}>
      <SentenceContainer gap={{ initial: '3', md: '6' }}>
        <Stack.Item>
          <Stack alignY="center" alignX="start" direction={{ initial: 'row' }} gap="3" wrap>
            {phrasesList.map(value => {
              if (!value.tagsList || !value.itemName) return null;

              return (
                <Stack.Item key={itemKey(value)}>
                  <FilterPhrase
                    alignX="start"
                    alignY="center"
                    direction={{ initial: 'row' }}
                    gap={{ initial: '1', lg: '3' }}
                    label=""
                    name={value.itemName}
                    onChange={event => setFilterPhraseValue(event.target.name, event.target.value)}
                    options={value.tagsList.reduce(
                      (acc, tag) => {
                        // A tag without an item name is a reference the migration could not resolve.
                        if (!tag.itemName) return acc;
                        acc.push({
                          // Sanity keeps no display name; its tags always carry their text.
                          label: tag.text ?? tag.displayName ?? tag.itemName,
                          value: tag.itemName,
                        });
                        return acc;
                      },
                      [
                        {
                          label: '',
                          value: '',
                        },
                      ],
                    )}
                    value={getFiltersFromSessionStorage(value.itemName) ?? ''}
                    placeholder=""
                    start={t(`filterSentence.${value.itemName}.label.start`)}
                    end={t(`filterSentence.${value.itemName}.label.end`)}
                    ariaLabel={t(`filterSentence.${value.itemName}.label.ariaLabel`)}
                  />
                </Stack.Item>
              );
            })}
          </Stack>
        </Stack.Item>
      </SentenceContainer>
    </Box>
  );
};
